import { useEffect, useId, useMemo, useRef } from "react";
import { useExternalDataStore } from "../../state/external-data-store";
import { filterClass } from "../../utils/class-utils";
import { TableInteractionNote } from "./shared/TableInteractionNote";
import {
  getAccordionButtonClass,
  getAccordionCollapseBaseClass,
  getAccordionItemClass,
  getAccordionToggleButtonProps,
  isEasyblocksEditorMode,
  resolveAccordionExpanded,
  resolveAccordionStartOpen,
} from "../utils/accordion-shell";
import type { NeighborhoodsAccordionProps, ResolvedRosterItem, RosterRowFilter, RosterSegmentKey } from "./types";

const truthyValues = new Set(["1", "y", "yes", "true", "on"]);
const rosterSegmentKeys: RosterSegmentKey[] = ["s_1", "s_2", "s_3", "s_4", "s_5", "s_6", "s_7", "s_8", "s_9"];

function isTruthy(value: unknown): boolean {
  if (value === true) return true;
  const normalized = String(value ?? "").toLowerCase().trim();
  return truthyValues.has(normalized);
}

function resolveToggleValue(value: boolean | string | undefined, defaultValue = true): boolean {
  if (value === undefined) return defaultValue;
  if (typeof value === "boolean") return value;
  return isTruthy(value);
}

function resolveNameParts(item: ResolvedRosterItem): { firstName: string; lastName: string } {
  const firstName = String(item.first_name ?? "").trim();
  const lastName = String(item.last_name ?? "").trim();
  if (firstName || lastName) return { firstName, lastName };

  const displayFromItem = String(item.item_name ?? item.name ?? item.title ?? "").trim();
  if (!displayFromItem) return { firstName: "", lastName: "Unknown" };

  const parts = displayFromItem.split(/\s+/).filter(Boolean);
  if (!parts.length) return { firstName: "", lastName: "Unknown" };
  if (parts.length === 1) return { firstName: "", lastName: parts[0] };
  const fallbackFirstName = parts.slice(0, -1).join(" ");
  const fallbackLastName = parts[parts.length - 1];
  return { firstName: fallbackFirstName, lastName: fallbackLastName };
}

function hasRole(item: ResolvedRosterItem, keys: string[]): boolean {
  return keys.some((key) => isTruthy(item[key]));
}

function resolveRowFilterMatch(item: ResolvedRosterItem, rowFilter: RosterRowFilter): boolean {
  if (rowFilter === "all") return true;
  if (rowFilter === "is_member") return hasRole(item, ["is_member", "member", "isMember"]);
  if (rowFilter === "is_board") {
    return hasRole(item, ["is_on_board", "board_member", "is_board_member", "isBoardMember"]);
  }
  if (rowFilter === "is_editor") return hasRole(item, ["is_editor"]);
  if (rowFilter === "is_poc") return hasRole(item, ["is_primary_poc"]);
  return isTruthy(item[rowFilter]);
}

function collectSegmentColumns(items: ResolvedRosterItem[]): Array<{ key: RosterSegmentKey; label: string }> {
  const segmentLabels: Partial<Record<RosterSegmentKey, string>> = {};

  for (const item of items) {
    const labels = item.segment_labels;
    if (!labels || typeof labels !== "object") continue;

    for (const key of rosterSegmentKeys) {
      if (!(key in labels) || segmentLabels[key] !== undefined) continue;
      segmentLabels[key] = String(labels[key] ?? "").trim();
    }
  }

  return rosterSegmentKeys
    .filter((key) => Object.prototype.hasOwnProperty.call(segmentLabels, key))
    .map((key) => ({
      key,
      label: segmentLabels[key] || key,
    }));
}

export function NeighborhoodsAccordion(props: NeighborhoodsAccordionProps) {
  const {
    bgClass = "no-class",
    paddingYClass = "py-section-md",
    title = "Neighborhood Resident List",
    viewMode = "accordion",
    accordionStartOpen = "false",
    showPhone = true,
    showEmail = true,
    showAddress = true,
    showRosterNotes = true,
    showIsMember = true,
    showIsBoard = true,
    showIsPoc = true,
    showIsEditor = true,
    showSegmentColumns = true,
    rowFilter = "all",
  } = props;

  const isStartOpen = resolveAccordionStartOpen(accordionStartOpen);

  const instanceIdRef = useRef<string>(`neighborhoods-${Math.random().toString(36).slice(2, 11)}`);
  const instanceId = instanceIdRef.current;

  const rosterState = useExternalDataStore((state) => state.roster[instanceId]);
  const ensureRoster = useExternalDataStore((state) => state.ensureRoster);
  const loadRoster = useExternalDataStore((state) => state.loadRoster);
  const resetRoster = useExternalDataStore((state) => state.resetRoster);

  const items = (rosterState?.items ?? []) as ResolvedRosterItem[];
  const isLoading = rosterState?.isLoading ?? true;
  const errorText = rosterState?.error ?? null;
  const reactId = useId();
  const accordionId = `neighborhoods-accordion-${reactId.replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const isInEditor = isEasyblocksEditorMode();

  const sectionClasses = [bgClass, filterClass(paddingYClass)]
    .map(filterClass)
    .filter(Boolean)
    .join(" ");

  /**
   * ===== CLASSES (frontend can edit here) =====
   */
  const classes = {
    section: ["section section-neighborhoods", sectionClasses].filter(Boolean).join(" ").trim(),
    container: "container-xl",
    title: "mb-3",
    tableWrap: "table-responsive",
    table: "table table-striped table-hover align-middle mb-0",
    checkIcon: "fa-solid fa-check",
    accordion: "accordion",
    accordionItem: getAccordionItemClass(),
    accordionHeader: "accordion-header",
    accordionButton: (expanded: boolean) => getAccordionButtonClass(isInEditor, expanded),
    accordionCollapse: getAccordionCollapseBaseClass(isInEditor),
    accordionBody: "accordion-body",
    empty: "",
    loading: "card shadow-sm",
    loadingBody: "card-body d-flex align-items-center gap-2",
    error: "alert alert-warning mb-0",
    editorNote: "alert alert-info mb-0",
  };

  const sortedItems = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => {
      const nameA = resolveNameParts(a);
      const nameB = resolveNameParts(b);
      const byLastName = nameA.lastName.toLowerCase().localeCompare(nameB.lastName.toLowerCase());
      if (byLastName !== 0) return byLastName;
      return nameA.firstName.toLowerCase().localeCompare(nameB.firstName.toLowerCase());
    });
    return copy;
  }, [items]);

  const filteredItems = useMemo(
    () => sortedItems.filter((item) => resolveRowFilterMatch(item, rowFilter)),
    [rowFilter, sortedItems]
  );

  const segmentColumns = useMemo(() => collectSegmentColumns(sortedItems), [sortedItems]);
  const shouldShowPhone = resolveToggleValue(showPhone);
  const shouldShowEmail = resolveToggleValue(showEmail);
  const shouldShowAddress = resolveToggleValue(showAddress);
  const shouldShowRosterNotes = resolveToggleValue(showRosterNotes);
  const shouldShowIsMember = resolveToggleValue(showIsMember);
  const shouldShowIsBoard = resolveToggleValue(showIsBoard);
  const shouldShowIsPoc = resolveToggleValue(showIsPoc);
  const shouldShowIsEditor = resolveToggleValue(showIsEditor);
  const shouldShowSegmentColumns = resolveToggleValue(showSegmentColumns);
  const resolvedTitle = String(title ?? "").trim() || "Neighborhood Resident List";

  useEffect(() => {
    ensureRoster(instanceId);
    void loadRoster(instanceId, 200);
    return () => resetRoster(instanceId);
  }, [ensureRoster, instanceId, loadRoster, resetRoster]);

  if (isLoading) {
    return (
      <section className={classes.section}>
        <div className={classes.container}>
          <div className={classes.loading}>
            <div className={classes.loadingBody}>
              <span className="spinner-border spinner-border-sm" aria-hidden="true" />
              Loading neighborhood residents...
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (errorText) {
    return (
      <section className={classes.section}>
        <div className={classes.container}>
          <div className={classes.error} role="alert">
            {errorText}
          </div>
        </div>
      </section>
    );
  }

  // In live view, hide the entire section if there's no data
  if (!filteredItems.length && !isInEditor) {
    return null;
  }

  const renderTable = () => (
    <div className={classes.tableWrap}>
      <div className="table-scroll">
        <table className={classes.table}>
        <thead className="text-bg-white sticky-top shadow-sm">
          <tr>
            <th scope="col">Last Name</th>
            <th scope="col">First Name</th>
            {shouldShowEmail ? <th scope="col">Email</th> : null}
            {shouldShowPhone ? <th scope="col">Mobile</th> : null}
            {shouldShowAddress ? <th scope="col">Address</th> : null}
            {shouldShowRosterNotes ? <th scope="col">Notes</th> : null}
            {shouldShowIsMember ? <th scope="col" className="text-center">Member</th> : null}
            {shouldShowIsBoard ? <th scope="col" className="text-center">Board Member</th> : null}
            {shouldShowIsPoc ? <th scope="col" className="text-center">Primary POC</th> : null}
            {shouldShowIsEditor ? <th scope="col" className="text-center">Editor</th> : null}
            {shouldShowSegmentColumns
              ? segmentColumns.map((segment) => (
                  <th key={segment.key} scope="col" className="text-center">
                    {segment.label}
                  </th>
                ))
              : null}
          </tr>
        </thead>
        <tbody>
          {filteredItems.map((item, idx) => {
            const { firstName, lastName } = resolveNameParts(item);
            const email = String(item.user_email ?? "").trim();
            const phone = String(item.mobile_phone ?? "").trim() || String(item.phone_home ?? "").trim();
            const address = String(item.address ?? "").trim();
            const notes = String(item.roster_notes ?? "").trim();
            const isMember = hasRole(item, ["is_member", "member", "isMember"]);
            const isBoardMember = hasRole(item, [
              "is_on_board",
              "board_member",
              "is_board_member",
              "isBoardMember",
            ]);
            const isPrimaryPoc = hasRole(item, ["is_primary_poc"]);
            const isEditor = hasRole(item, ["is_editor"]);

            return (
              <tr key={item.__id ?? item.KD_user_item_id ?? idx}>
                <td>{lastName || <span>-</span>}</td>
                <td>{firstName || <span>-</span>}</td>
                {shouldShowEmail ? (
                  <td className="text-nowrap">
                    {email ? (
                      <a href={`mailto:${email}`}>{email}</a>
                    ) : (
                      <span>-</span>
                    )}
                  </td>
                ) : null}
                {shouldShowPhone ? (
                  <td className="text-nowrap">{phone || <span>-</span>}</td>
                ) : null}
                {shouldShowAddress ? <td>{address || <span>-</span>}</td> : null}
                {shouldShowRosterNotes ? <td>{notes || <span>-</span>}</td> : null}
                {shouldShowIsMember ? (
                  <td className="text-center">{isMember ? <i className={classes.checkIcon} aria-label="Member" /> : null}</td>
                ) : null}
                {shouldShowIsBoard ? (
                  <td className="text-center">{isBoardMember ? <i className={classes.checkIcon} aria-label="Board Member" /> : null}</td>
                ) : null}
                {shouldShowIsPoc ? (
                  <td className="text-center">{isPrimaryPoc ? <i className={classes.checkIcon} aria-label="Primary POC" /> : null}</td>
                ) : null}
                {shouldShowIsEditor ? (
                  <td className="text-center">{isEditor ? <i className={classes.checkIcon} aria-label="Editor" /> : null}</td>
                ) : null}
                {shouldShowSegmentColumns
                  ? segmentColumns.map((segment) => (
                      <td key={segment.key} className="text-center">
                        {isTruthy(item[segment.key]) ? <i className={classes.checkIcon} aria-label={segment.label} /> : null}
                      </td>
                    ))
                  : null}
              </tr>
            );
          })}
        </tbody>
        </table>
      </div>
      <TableInteractionNote />
    </div>
  );

  const renderEditorNote = () => (
    <div className={classes.editorNote} role="status">
      Neighborhood residents are hidden in edit mode. Filters and optional columns will appear in the live view.
    </div>
  );

  const renderNoDataNote = () => (
    <div className={classes.editorNote} role="status">
      {sortedItems.length === 0
        ? "No neighborhood residents available at this time. This section will not appear in the live view until resident data is available."
        : "No neighborhood residents match the current filter. This section will stay hidden in the live view until matching resident data is available."}
    </div>
  );

  const renderEditorContent = () =>
    filteredItems.length === 0 ? renderNoDataNote() : renderEditorNote();

  const isAccordionExpanded = resolveAccordionExpanded(isInEditor, isStartOpen);

  return (
    <section className={classes.section}>
      <div className={classes.container}>
        {viewMode === "accordion" ? (
          <div className={classes.accordion} id={accordionId}>
            <div className={classes.accordionItem}>
              <h2 className={classes.accordionHeader} id={`${accordionId}-heading`}>
                <button
                  className={classes.accordionButton(isAccordionExpanded)}
                  type="button"
                  aria-expanded={isAccordionExpanded ? "true" : "false"}
                  aria-controls={`${accordionId}-collapse`}
                  {...getAccordionToggleButtonProps(isInEditor, `${accordionId}-collapse`)}
                >
                  <span className="h4 mb-0">{resolvedTitle}</span>
                </button>
              </h2>
              <div
                id={`${accordionId}-collapse`}
                className={[
                  classes.accordionCollapse,
                  isInEditor ? "show" : isStartOpen ? "show" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                aria-labelledby={`${accordionId}-heading`}
              >
                <div className={classes.accordionBody}>{isInEditor ? renderEditorContent() : renderTable()}</div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <h2 className={classes.title}>{resolvedTitle}</h2>
            {isInEditor ? renderEditorContent() : renderTable()}
          </>
        )}
      </div>
    </section>
  );
}

export default NeighborhoodsAccordion;
